(ns io.daskul.webapp.auth.throttle
  "Pure logic for the login rate-limit lockout UX (SMS-280).

   The login endpoint answers a lockout with HTTP 429 + {:retry-after secs}
   instead of the usual 401 wrong-credentials. These reducers turn that into a
   ticking countdown held in [:auth :retry-after], kept distinct from
   [:auth :error], so the view can say 'wait N seconds' rather than show a
   generic error identical to a bad password.

   React-free and clock-free, so they run under the :node-test build; the
   product event handlers are thin wrappers that translate :start-tick? /
   :continue? into a re-frame :dispatch-later effect.")

(def tick-ms
  "Countdown cadence — one second between ticks."
  1000)

(defn rate-limited?
  "Is this ajax login failure a rate-limit lockout (HTTP 429)?"
  [{:keys [status]}]
  (= 429 status))

(defn apply-login-failure
  "Reduce an ajax login failure into the next db + whether to start a countdown.

   Returns {:db next-db, :start-tick? bool}. For a 429: set [:auth :retry-after]
   from the response and clear [:auth :error]; a countdown is (re)started only
   when one isn't already running (so a resubmit doesn't spawn a second ticking
   loop). Any other failure sets [:auth :error] and clears any countdown."
  [db {:keys [response] :as failure}]
  (if (rate-limited? failure)
    {:db (-> db
             (assoc-in [:auth :loading?] false)
             (assoc-in [:auth :error] nil)
             (assoc-in [:auth :retry-after] (:retry-after response)))
     :start-tick? (nil? (get-in db [:auth :retry-after]))}
    {:db (-> db
             (assoc-in [:auth :loading?] false)
             (assoc-in [:auth :retry-after] nil)
             (assoc-in [:auth :error] (or (:message response) "Login failed")))
     :start-tick? false}))

(defn apply-throttle-tick
  "Reduce one countdown tick. Returns {:db next-db, :continue? bool}. Decrements
   [:auth :retry-after]; clears it (and stops) once it reaches zero."
  [db]
  (let [remaining (dec (or (get-in db [:auth :retry-after]) 0))]
    (if (pos? remaining)
      {:db (assoc-in db [:auth :retry-after] remaining) :continue? true}
      {:db (assoc-in db [:auth :retry-after] nil) :continue? false})))

(defn countdown-message
  "Human countdown string for `secs` remaining on a login lockout."
  [secs]
  (str "Please wait " secs (if (= 1 secs) " second" " seconds")
       " before trying again."))
